/**
 * VideoTile — a video, played when the person presses play.
 *
 * data: { url: string; poster?; duration?; title?; source? }
 *
 * A YouTube watch / youtu.be / embed URL plays in a youtube-nocookie
 * iframe whose src we build from the extracted id; anything else plays in
 * an HTML5 `<video>`. Nothing is fetched before the click — not even the
 * poster: an agent-written URL that the browser loads on render is an
 * exfiltration channel (see ImageTile), so the tile shows the host and
 * the play button instead.
 */
import * as React from "react";
import { Play } from "lucide-react";
import { hostOf, safeHttpUrl } from "@/lib/safe-url";
import type { TileBodyProps } from "../registry";

interface VideoData {
  url?: string;
  title?: string;
  duration?: string;
  source?: string;
}

export function VideoTile({ data }: TileBodyProps) {
  const d = data as VideoData;
  const [playing, setPlaying] = React.useState(false);
  const url = safeHttpUrl(d.url);
  const youTubeId = React.useMemo(() => extractYouTubeId(url), [url]);

  if (playing && youTubeId) {
    return (
      <iframe
        className="h-full w-full border-0"
        src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(youTubeId)}?autoplay=1&rel=0`}
        title={d.title ?? "Video"}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (playing && url) {
    return <video className="h-full w-full bg-bg-sunken object-contain" src={url} controls autoPlay />;
  }

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-sunken">
      <button
        type="button"
        onClick={() => setPlaying(true)}
        disabled={!url}
        aria-label="Play video"
        className="flex h-12 w-12 items-center justify-center bg-fg text-bg transition hover:bg-accent hover:text-accent-fg disabled:opacity-40"
      >
        <Play size={20} />
      </button>
      <div className="max-w-full truncate px-3 text-center font-mono text-[10px] text-fg-faint">
        {[d.source || (url ? hostOf(url) : "no video"), d.duration].filter(Boolean).join(" · ")}
      </div>
      {d.title && (
        <div className="absolute inset-x-3 bottom-2 truncate text-center text-[11.5px] text-fg-muted">
          {d.title}
        </div>
      )}
    </div>
  );
}

/**
 * The YouTube id from a watch URL (`?v=…`), a youtu.be short URL or an
 * /embed/… URL; `null` for anything else (played as HTML5 video).
 */
export function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") return u.pathname.slice(1) || null;
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.slice(7) || null;
      if (u.pathname.startsWith("/shorts/")) return u.pathname.slice(8) || null;
    }
  } catch {
    /* not a URL */
  }
  return null;
}
